import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const TABS = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "news", label: "资讯" },
];

function highlight(text: string, keyword: string) {
  if (!keyword) return text;
  const index = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <span className="text-accent-600">{text.slice(index, index + keyword.length)}</span>
      {text.slice(index + keyword.length)}
    </>
  );
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState("all");

  usePageMeta(
    keyword ? `搜索"${keyword}" - 后入影视网` : "搜索 - 后入影视网",
    keyword
      ? `后入影视网搜索"${keyword}"的结果，包含相关电影、电视剧与影视资讯。`
      : "后入影视网全站搜索，支持电影、电视剧与影视资讯关键词检索。",
    "影视搜索,电影搜索,电视剧搜索,资讯搜索,后入影视"
  );

  useEffect(() => {
    setInput(keyword);
    setTab("all");
  }, [keyword]);

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    return movies.filter(
      (item) =>
        item.title.includes(keyword) ||
        item.region.includes(keyword) ||
        item.director.includes(keyword) ||
        item.genres.some((genre) => genre.includes(keyword)) ||
        item.actors.some((actor) => actor.includes(keyword))
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    return news.filter(
      (item) =>
        item.title.includes(keyword) ||
        item.summary.includes(keyword) ||
        item.category.includes(keyword) ||
        item.content.some((paragraph) => paragraph.includes(keyword))
    );
  }, [keyword]);

  const filteredMovies =
    tab === "news"
      ? []
      : movieResults.filter((item) => tab === "all" || item.type === tab);
  const filteredNews = tab === "all" || tab === "news" ? newsResults : [];

  const totalCount = filteredMovies.length + filteredNews.length;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  return (
    <div className="pb-16">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            全站搜索
          </h1>

          <form
            onSubmit={handleSubmit}
            className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3">
              <i className="ri-search-line text-lg text-secondary-500"></i>
              <input
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入影视名称、类型、导演、演员或资讯关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-secondary-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full px-4 md:px-6">
        {keyword ? (
          <>
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-secondary-600">
                关键词
                <span className="mx-1 font-semibold text-foreground-950">
                  "{keyword}"
                </span>
                找到
                <span className="mx-1 font-semibold text-accent-600">
                  {totalCount}
                </span>
                条结果
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
                {TABS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                      tab === item.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:text-accent-600"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {totalCount > 0 ? (
              <div className="mt-6 flex flex-col gap-10">
                {filteredMovies.length > 0 && (
                  <section>
                    <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <i className="ri-film-line text-accent-500"></i>
                      影视结果（{filteredMovies.length}）
                    </h2>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
                      {filteredMovies.map((item) => (
                        <MovieCard key={item.id} item={item} showType />
                      ))}
                    </div>
                  </section>
                )}

                {filteredNews.length > 0 && (
                  <section>
                    <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <i className="ri-newspaper-line text-accent-500"></i>
                      资讯结果（{filteredNews.length}）
                    </h2>
                    <ul className="flex flex-col gap-4">
                      {filteredNews.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/news/${item.id}`}
                            className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 sm:flex-row md:p-5"
                          >
                            <span className="h-36 w-full shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-24 sm:w-40">
                              <img
                                src={item.cover}
                                alt={`${item.title} 资讯配图`}
                                title={`${item.title} 影视资讯`}
                                className="h-full w-full object-cover object-center"
                              />
                            </span>
                            <span className="flex flex-1 flex-col">
                              <span className="text-xs text-secondary-500">
                                {item.category} · {item.date}
                              </span>
                              <span className="mt-2 text-base font-semibold text-foreground-950 group-hover:text-accent-600">
                                {highlight(item.title, keyword)}
                              </span>
                              <span className="mt-2 line-clamp-2 text-sm text-secondary-600">
                                {item.summary}
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            ) : (
              <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-secondary-400">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  没有找到相关结果
                </p>
                <p className="mt-2 text-sm text-secondary-500">
                  换个关键词再试试，或返回首页浏览推荐内容
                </p>
                <Link
                  to="/"
                  className="mt-5 flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-secondary-400">
              <i className="ri-search-2-line text-3xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
              开始你的搜索
            </p>
            <p className="mt-2 text-sm text-secondary-500">
              输入影视名称、类型、导演或资讯关键词，快速找到想看的内容
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {["科幻", "悬疑", "古装", "爱情", "都市迷城"].map((hint) => (
                <button
                  key={hint}
                  type="button"
                  onClick={() => setSearchParams({ q: hint })}
                  className="whitespace-nowrap rounded-full bg-background-100 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-accent-50 hover:text-accent-600"
                >
                  {hint}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}